import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { isIOS } from '../../services/native';
import {
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  ArrowDownLeft,
  Plus,
  ArrowRight,
  ChevronRight,
  Calculator,
  Percent,
  Download,
  Book,
  BookOpen,
  CalendarDays,
  Building2,
  BarChart3,
  Palette,
  AlertTriangle,
  Repeat,
  Lock,
  Sparkles,
  Wallet,
  Banknote,
  CreditCard,
  MoreHorizontal,
  Wifi,
  Bell,
  Info, ScanLine } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
import { usePWA } from '../../context/PWAContext';
import { useTheme } from '../../context/ThemeContext';
import { useSummaryQuery, useCategoryBreakdownQuery, useTransactionsQuery, useStatsQuery, useAccountsWithBalancesQuery, useCategoriesQuery, useNotificationUnreadCountQuery, useRecurringTransactionsQuery, useMonthlyTrendQuery } from '../../hooks/useQueries';
import CategoryProgressBars from './CategoryProgressBars';
import MonthlyTrendChart from './MonthlyTrendChart';
import InsightsModal from '../insights/InsightsModal';
import ActionableInsights from './ActionableInsights';
import { DashboardSkeleton } from '../common/Skeleton';
import { BUDGET_ALERT_THRESHOLD } from '../../utils/constants';
import { getIconComponent } from '../common/IconPicker';
import ScanPayCard from '../pay/ScanPayCard';
import { isScanPayAvailable } from '../../utils/upi';

export default function Dashboard() {
  const { user, hasEncryptionKey } = useAuth();
  const { formatAmount, formatDate, formatTime, config } = useConfig();
  const { canInstall, installPWA } = usePWA();
  const { isModernUI } = useTheme();
  const scanPayAvailable = isScanPayAvailable(config);
  const navigate = useNavigate();
  const location = useLocation();
  const isTaxUser = user?.email === import.meta.env.VITE_TAX_ALLOWED_EMAIL;
  const financialYear = user?.financialYear || '2025-26';
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);
  const [transactionFilter, setTransactionFilter] = useState('all');
  // Info tooltip state
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipTimeout = useRef(null);
  const toggleTooltip = useCallback((id) => {
    if (tooltipTimeout.current) clearTimeout(tooltipTimeout.current);
    if (activeTooltip === id) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(id);
      tooltipTimeout.current = setTimeout(() => setActiveTooltip(null), 3000);
    }
  }, [activeTooltip]);

  // Restore filter when navigating back from transaction detail
  useEffect(() => {
    const saved = sessionStorage.getItem('dashboard_tx_filter');
    if (saved) {
      sessionStorage.removeItem('dashboard_tx_filter');
      setTransactionFilter(saved);
    }
  }, [location.key]);

  // Reset filter on pull-to-refresh
  useEffect(() => {
    const handleRefresh = () => setTransactionFilter('all');
    window.addEventListener('dashboard-refresh', handleRefresh);
    return () => window.removeEventListener('dashboard-refresh', handleRefresh);
  }, []);

  // Compute current month date range for filtering
  const currentMonthDates = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    // Pad month/day to avoid timezone issues with toISOString()
    const startDate = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const endDate = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate };
  }, []);

  // React Query hooks - data is cached automatically
  const { data: summary, isLoading: summaryLoading } = useSummaryQuery(
    currentMonthDates,
    { enabled: hasEncryptionKey }
  );

  // Category breakdown filtered to current month
  const { data: categoryBreakdown } = useCategoryBreakdownQuery(
    currentMonthDates,
    { enabled: hasEncryptionKey }
  );

  const { data: transactionData, isLoading: transactionsLoading } = useTransactionsQuery(
    { limit: 5, sortBy: 'date', sortOrder: 'desc', startDate: currentMonthDates.startDate, endDate: currentMonthDates.endDate },
    { enabled: hasEncryptionKey }
  );

  // Fetch stats for budget alerts (thisMonth = calendar month)
  const { data: statsData } = useStatsQuery(
    { type: 'expense', period: 'thisMonth' },
    { enabled: hasEncryptionKey }
  );

  // Fetch monthly trend data for chart
  const { data: trendData } = useMonthlyTrendQuery({ period: 'thisMonth' }, {
    enabled: hasEncryptionKey
  });

  // Fetch accounts with balances for account breakdown
  const { data: accounts = [] } = useAccountsWithBalancesQuery({
    enabled: hasEncryptionKey
  });

  // Fetch categories for icons
  const { data: categories = [] } = useCategoriesQuery({
    enabled: hasEncryptionKey
  });

  // Fetch notification unread count
  const { data: unreadCount = 0 } = useNotificationUnreadCountQuery({
    enabled: hasEncryptionKey
  });

  // Fetch recurring transactions for actionable insights
  const { data: recurringData = {} } = useRecurringTransactionsQuery({}, {
    enabled: hasEncryptionKey
  });
  const recurringTransactionsForInsights = recurringData.recurringTransactions || [];

  // Helper to get category info (icon, color)
  const getCategoryInfo = (categoryName, type) => {
    const category = categories.find(c => c.name === categoryName && c.type === type);
    if (category) {
      return {
        icon: getIconComponent(category.icon),
        color: category.color || (type === 'income' ? '#22c55e' : '#ef4444')
      };
    }
    // Fallback for transfer or unknown
    return {
      icon: type === 'transfer' ? ArrowRight : (type === 'income' ? TrendingUp : TrendingDown),
      color: type === 'transfer' ? '#6366f1' : (type === 'income' ? '#22c55e' : '#ef4444')
    };
  };

  const recentTransactions = transactionData?.transactions?.slice(0, 5) || [];
  const loading = summaryLoading;

  // Calculate budget alerts (categories at 90%+ or over budget)
  const budgetAlerts = useMemo(() => {
    if (!statsData?.categoryBreakdown?.expense) return [];

    const alerts = [];
    Object.entries(statsData.categoryBreakdown.expense).forEach(([category, data]) => {
      if (data.budget && data.budget > 0) {
        const percentage = Math.round((data.total / data.budget) * 100);
        if (percentage >= BUDGET_ALERT_THRESHOLD) {
          alerts.push({
            name: category,
            spent: data.total,
            budget: data.budget,
            budgetPeriod: data.budgetPeriod || 'monthly',
            percentage,
            isOverBudget: percentage > 100,
            color: data.color || '#6366f1',
            count: data.count
          });
        }
      }
    });

    // Sort by percentage descending (most over budget first)
    return alerts.sort((a, b) => b.percentage - a.percentage);
  }, [statsData]);

  // Filter out hidden accounts for display and balance calculations.
  // Server's canonical flag is `isHidden`; older records used
  // `isActive: false` for the same thing, so accept either.
  const visibleAccounts = accounts.filter(
    a => a.isHidden !== true && a.isActive !== false,
  );

  // Calculate total balance from account balances (includes transfers)
  const totalBalance = visibleAccounts.reduce((sum, acc) => sum + (acc.currentBalance ?? 0), 0);
  const totalIncome = (summary?.totalIncome || 0) - (isTaxUser ? (summary?.totalTDS || 0) : 0);
  const totalExpense = summary?.totalExpense || 0;

  // Show skeleton on initial load (no cached data)
  if (loading && !summary) {
    return <DashboardSkeleton />;
  }

  return (
    <>
      {/* Mobile Layout - Banking Style */}
      <div className="xl:hidden -mx-4" style={{ marginTop: isIOS ? 'calc(-24px - var(--safe-area-top, 0px))' : '-32px' }}>
        {/* Extended Dark Header Section */}
        <div
          className="relative overflow-hidden pb-6"
          style={{ backgroundColor: 'var(--accent-bg)' }}
        >
          {/* Decorative background shapes - diamonds and hexagons */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Large rotating diamond */}
            <div
              className="absolute -right-16 -top-16 w-48 h-48 opacity-10 animate-spin-slow"
              style={{
                backgroundColor: 'var(--accent-text)',
                transform: 'rotate(45deg)',
                borderRadius: '24px'
              }}
            />
            {/* Medium pulsing hexagon-ish blob */}
            <div
              className="absolute -left-12 top-24 w-36 h-36 opacity-10 animate-pulse-slow"
              style={{
                backgroundColor: 'var(--accent-text)',
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
              }}
            />
            {/* Small floating triangle */}
            <div
              className="absolute right-8 bottom-16 w-20 h-20 opacity-10 animate-float-fast"
              style={{
                backgroundColor: 'var(--accent-text)',
                clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)'
              }}
            />
          </div>

          {/* Top Bar - Status icons */}
          <div className={`relative px-4 pb-2 ${isIOS ? 'sticky top-0 z-30' : 'pt-4'}`} style={isIOS ? { paddingTop: 'calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 16px)', backgroundColor: 'var(--accent-bg)' } : undefined}>
            <div className="flex items-center justify-between">
              {/* Left: Logo + Welcome */}
              <div className="flex items-center gap-2.5">
                <img
                  src="/logo.png"
                  alt="Hisab Journey"
                  className="w-9 h-9 rounded-xl object-contain bg-white/10 p-1"
                />
                <div>
                  <p className="text-[10px] font-medium opacity-70" style={{ color: 'var(--accent-text)' }}>Welcome back</p>
                  <h2 className="text-sm font-bold" style={{ color: 'var(--accent-text)' }}>
                    {user?.name?.split(' ')[0] || 'User'}
                  </h2>
                </div>
              </div>
              {/* Right: Action icons + FY badge */}
              <div className="flex items-center gap-1.5">
                {canInstall && (
                  <button
                    onClick={installPWA}
                    className="w-8 h-8 rounded-xl flex items-center justify-center bg-white/10 backdrop-blur-sm active:scale-95 transition-transform"
                    title="Install App"
                  >
                    <Download className="w-4 h-4" style={{ color: 'var(--accent-text)' }} />
                  </button>
                )}
                <button
                  onClick={() => navigate('/notifications')}
                  className="w-8 h-8 rounded-xl flex items-center justify-center bg-white/10 backdrop-blur-sm relative active:scale-95 transition-transform"
                  title="Notifications"
                >
                  <span className="text-sm">🔔</span>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setIsInsightsOpen(true)}
                  className="w-8 h-8 rounded-xl flex items-center justify-center bg-white/10 backdrop-blur-sm active:scale-95 transition-transform"
                  title="HJ Insights"
                >
                  <span className="text-sm">✨</span>
                </button>
{/* FY Badge - DISABLED for simplicity
                <span
                  className="px-2 py-1 rounded-lg text-[10px] font-bold bg-white/15 backdrop-blur-sm"
                  style={{ color: 'var(--accent-text)' }}
                >
                  FY {financialYear}
                </span>
*/}
              </div>
            </div>
          </div>

          {/* Main Balance Display */}
          <div className="relative px-4 pt-4 pb-2 text-center">
            <p className="text-xs font-medium opacity-70 mb-1" style={{ color: 'var(--accent-text)' }}>
              Available Balance
            </p>
            <p className="text-3xl font-bold tracking-tight" style={{ color: 'var(--accent-text)' }}>
              {formatAmount(totalBalance)}
            </p>
            {/* Income/Expense summary below balance */}
            <div className="flex items-center justify-center gap-4 mt-3">
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center">
                  <TrendingUp className="w-3.5 h-3.5 text-green-400" />
                </div>
                <span className="text-xs font-semibold text-green-400">{formatAmount(totalIncome)}</span>
              </div>
              <div className="w-px h-4 bg-white/20" />
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center">
                  <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                </div>
                <span className="text-xs font-semibold text-red-400">{formatAmount(totalExpense)}</span>
              </div>
            </div>
          </div>

          {/* Account Balance Cards + Quick Actions */}
          <div className="relative px-4 pt-4">
            {visibleAccounts.length === 1 ? (
              /* Single account: Show account card + quick action buttons */
              <div className="flex justify-center gap-3">
                {/* Single Account Card */}
                {(() => {
                  const account = visibleAccounts[0];
                  const isCard = account.type === 'credit_card';
                  const accountColor = account.color || '#3b82f6';

                  if (isCard) {
                    return (
                      <div
                        onClick={() => navigate('/accounts')}
                        className="flex-1 max-w-[160px] h-[80px] rounded-xl relative overflow-hidden cursor-pointer active:scale-[0.98] transition-all"
                        style={{
                          background: `linear-gradient(135deg, ${accountColor}ee 0%, ${accountColor}bb 100%)`,
                          boxShadow: `0 4px 12px ${accountColor}40`,
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent h-1/2" />
                        <div className="absolute top-2.5 left-2.5 w-5 h-3.5 rounded-[2px] bg-gradient-to-br from-amber-300 to-amber-500 shadow-sm" />
                        <div className="absolute top-2.5 right-2.5">
                          <Wifi className="w-3.5 h-3.5 text-white/50 rotate-90" />
                        </div>
                        <div className="absolute bottom-2.5 left-2.5 right-2.5">
                          <p className="text-white/80 text-[9px] font-semibold uppercase tracking-wider truncate mb-0.5">
                            {account.name}
                          </p>
                          <p className="text-white text-base font-bold">
                            {formatAmount(account.currentBalance ?? 0)}
                          </p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      onClick={() => navigate('/accounts')}
                      className="flex-1 max-w-[160px] h-[80px] rounded-xl p-3 relative overflow-hidden cursor-pointer active:scale-[0.98] transition-all bg-white/15 backdrop-blur-sm border border-white/20"
                    >
                      <div
                        className="absolute -top-3 -right-3 w-12 h-12 rounded-full blur-xl opacity-30"
                        style={{ backgroundColor: accountColor }}
                      />
                      <p className="text-white/70 text-[10px] font-medium truncate mb-1">
                        {account.name}
                      </p>
                      <p className="text-white text-lg font-bold">
                        {formatAmount(account.currentBalance ?? 0)}
                      </p>
                    </div>
                  );
                })()}

                {/* Quick Action Buttons for single account */}
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate('/transactions/new?type=income')}
                    className="w-[76px] h-[80px] rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 flex flex-col items-center justify-center gap-1.5 active:scale-95 transition-transform"
                  >
                    <TrendingUp className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
                    <span className="text-[10px] font-medium" style={{ color: 'var(--accent-text)' }}>Income</span>
                  </button>
                  <button
                    onClick={() => navigate('/transactions/new?type=expense')}
                    className="w-[76px] h-[80px] rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 flex flex-col items-center justify-center gap-1.5 active:scale-95 transition-transform"
                  >
                    <TrendingDown className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
                    <span className="text-[10px] font-medium" style={{ color: 'var(--accent-text)' }}>Expense</span>
                  </button>
                </div>
              </div>
            ) : visibleAccounts.length > 0 ? (
              /* Multiple accounts: Show all in scroll */
              <>
                <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
                  {[...visibleAccounts].sort((a, b) => (a.currentBalance ?? 0) - (b.currentBalance ?? 0)).slice(0, 6).map((account) => {
                    const isCard = account.type === 'credit_card';
                    const accountColor = account.color || '#3b82f6';

                    if (isCard) {
                      return (
                        <div
                          key={account._id}
                          onClick={() => navigate('/accounts')}
                          className="flex-shrink-0 w-[130px] h-[75px] rounded-xl relative overflow-hidden cursor-pointer active:scale-[0.98] transition-all"
                          style={{
                            background: `linear-gradient(135deg, ${accountColor}ee 0%, ${accountColor}bb 100%)`,
                            boxShadow: `0 4px 12px ${accountColor}40`,
                          }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent h-1/2" />
                          <div className="absolute top-2 left-2 w-4 h-3 rounded-[2px] bg-gradient-to-br from-amber-300 to-amber-500 shadow-sm" />
                          <div className="absolute top-2 right-2">
                            <Wifi className="w-3 h-3 text-white/50 rotate-90" />
                          </div>
                          <div className="absolute bottom-2 left-2 right-2">
                            <p className="text-white/80 text-[9px] font-semibold uppercase tracking-wider truncate mb-0.5">
                              {account.name}
                            </p>
                            <p className="text-white text-sm font-bold">
                              {formatAmount(account.currentBalance ?? 0)}
                            </p>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={account._id}
                        onClick={() => navigate('/accounts')}
                        className="flex-shrink-0 w-[130px] h-[75px] rounded-xl p-2.5 relative overflow-hidden cursor-pointer active:scale-[0.98] transition-all bg-white/15 backdrop-blur-sm border border-white/20"
                      >
                        <div
                          className="absolute -top-3 -right-3 w-12 h-12 rounded-full blur-xl opacity-30"
                          style={{ backgroundColor: accountColor }}
                        />
                        <p className="text-white/70 text-[9px] font-medium truncate mb-1">
                          {account.name}
                        </p>
                        <p className="text-white text-base font-bold mt-1">
                          {formatAmount(account.currentBalance ?? 0)}
                        </p>
                      </div>
                    );
                  })}
                </div>
                {visibleAccounts.length > 6 && (
                  <button
                    onClick={() => navigate('/accounts')}
                    className="mt-2 text-[10px] font-medium opacity-80 hover:opacity-100 transition-opacity mx-auto block"
                    style={{ color: 'var(--accent-text)' }}
                  >
                    +{visibleAccounts.length - 6} more accounts
                  </button>
                )}
              </>
            ) : (
              /* No accounts: Show quick action buttons */
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => navigate('/transactions/new?type=income')}
                  className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                    <TrendingUp className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
                  </div>
                  <span className="text-[10px] font-medium" style={{ color: 'var(--accent-text)' }}>Income</span>
                </button>
                <button
                  onClick={() => navigate('/transactions/new?type=expense')}
                  className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                    <TrendingDown className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
                  </div>
                  <span className="text-[10px] font-medium" style={{ color: 'var(--accent-text)' }}>Expense</span>
                </button>
                <button
                  onClick={() => navigate('/transactions/new?type=transfer')}
                  className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                    <ArrowRight className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
                  </div>
                  <span className="text-[10px] font-medium" style={{ color: 'var(--accent-text)' }}>Transfer</span>
                </button>
                <button
                  onClick={() => navigate('/accounts')}
                  className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                    <Building2 className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
                  </div>
                  <span className="text-[10px] font-medium" style={{ color: 'var(--accent-text)' }}>Accounts</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* White Content Section - with rounded top */}
        <div className="bg-surface-50 -mt-4 rounded-t-3xl relative z-10 pt-4 min-h-screen">

        {/* Scan & Pay - Mobile (India / INR only) */}
        {scanPayAvailable && (
          <div className={isModernUI ? "px-4 mt-3" : "px-4 mt-4"}>
            <ScanPayCard compact={isModernUI} />
          </div>
        )}

        {/* Quick Links - Mobile (icon grid) */}
        <div className={isModernUI ? "px-4 mt-3" : "px-4 mt-4"}>
          <div className="grid grid-cols-5 gap-y-3 gap-x-1">
            {[
              { to: '/transactions', emoji: '📒', label: 'Txns', color: '#8b5cf6' },
              { to: '/recurring', emoji: '🔁', label: 'Recurring', color: '#f59e0b' },
              { to: '/receivables', emoji: '💵', label: 'Receivable', color: '#10b981' },
              { to: '/debts', emoji: '🏦', label: 'Debts', color: '#ef4444' },
              { to: '/categories', emoji: '🏷️', label: 'Categories', color: '#f97316' },
              { to: '/trend', emoji: '📈', label: 'Trend', color: '#6366f1' },
              { to: '/ledger', emoji: '📖', label: 'Ledger', color: '#0ea5e9' },
              { to: '/passcode', emoji: '🔒', label: 'App Lock', color: '#64748b' },
              { to: '/settings/theme', emoji: '🎨', label: 'Theme', color: '#ec4899' },
              { to: '/configuration', emoji: '⚙️', label: 'Config', color: '#14b8a6' },
            ].map(({ to, emoji, label, color }) => (
              <Link
                key={to}
                to={to}
                className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
              >
                <div
                  className={isModernUI ? "w-10 h-10 rounded-xl flex items-center justify-center" : "w-11 h-11 rounded-xl flex items-center justify-center"}
                  style={{ backgroundColor: `${color}12` }}
                >
                  <span className={isModernUI ? "text-lg" : "text-xl"}>{emoji}</span>
                </div>
                <span className="text-[10px] font-medium text-surface-500 text-center leading-tight">{label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Budget Alerts - Mobile (Dynamic layout: 1=full, 2=half, 3+=scroll) */}
        {budgetAlerts.length > 0 && (
          <div className={isModernUI ? "px-4 mt-3" : "px-4 mt-4"}>
            <div className={isModernUI ? "flex items-center justify-between mb-2" : "flex items-center justify-between mb-3"}>
              <div className={isModernUI ? "flex items-center gap-1.5" : "flex items-center gap-2"}>
                <div className={isModernUI ? "w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center" : "w-8 h-8 bg-amber-100 rounded-xl flex items-center justify-center"}>
                  <AlertTriangle className={isModernUI ? "w-3.5 h-3.5 text-amber-600" : "w-4 h-4 text-amber-600"} />
                </div>
                <div>
                  <h3 className={isModernUI ? "text-xs font-bold text-surface-900" : "text-sm font-semibold text-surface-900"}>Budget Alerts</h3>
                  <p className={isModernUI ? "text-[9px] text-surface-400" : "text-[10px] text-surface-400"}>{budgetAlerts.length} categories need attention</p>
                </div>
              </div>
              <Link
                to="/stats?type=expense"
                className={isModernUI ? "text-[10px] font-semibold px-2 py-0.5 rounded-md" : "text-xs font-medium px-2.5 py-1 rounded-lg"}
                style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-bg)' }}
              >
                Stats
              </Link>
            </div>
            {/* 1 item = full width, 2 items = grid, 3+ = horizontal scroll */}
            <div className={
              budgetAlerts.length === 1
                ? 'grid grid-cols-1 gap-2.5'
                : budgetAlerts.length === 2
                  ? 'grid grid-cols-2 gap-2.5'
                  : 'flex gap-2.5 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide'
            }>
              {budgetAlerts.map((alert) => {
                const isOver = alert.isOverBudget;
                const overBy = alert.spent - alert.budget;
                const progressWidth = Math.min(alert.percentage, 100);
                return (
                  <button
                    key={alert.name}
                    onClick={() => navigate('/budget-category', { state: { category: alert } })}
                    className={`${budgetAlerts.length > 2 ? 'flex-shrink-0 w-[160px]' : ''} rounded-2xl p-3 text-left transition-all active:scale-[0.98] ${
                      isOver ? 'bg-red-50 border border-red-100' : 'bg-amber-50 border border-amber-100'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: alert.color }}
                      />
                      <span className="font-semibold text-surface-900 text-sm truncate">{alert.name}</span>
                    </div>
                    {/* Progress bar */}
                    <div className={`h-1.5 rounded-full mb-2 ${isOver ? 'bg-red-200' : 'bg-amber-200'}`}>
                      <div
                        className={`h-full rounded-full transition-all ${isOver ? 'bg-red-500' : 'bg-amber-500'}`}
                        style={{ width: `${progressWidth}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-bold ${isOver ? 'text-red-600' : 'text-amber-600'}`}>
                        {isOver ? `+${formatAmount(overBy)}` : `${alert.percentage}%`}
                      </span>
                      <span className="text-[10px] text-surface-500">
                        {formatAmount(alert.budget)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Actionable Insights - Mobile */}
        <div className="px-4 mt-3">
          <ActionableInsights
            statsData={statsData}
            summary={summary}
            recurringTransactions={recurringTransactionsForInsights}
            formatAmount={formatAmount}
          />
        </div>

        {/* Recent Transactions - Mobile */}
        <div className="px-4 mt-4">
          {/* Header with See All */}
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold text-surface-900">Recent Transactions</h3>
            <Link
              to="/transactions"
              className="text-xs font-medium"
              style={{ color: 'var(--accent-bg)' }}
            >
              See all
            </Link>
          </div>

          {/* Filter Tabs */}
          <div className="grid grid-cols-3 gap-1 p-1 bg-surface-100 rounded-xl mb-2.5">
            {[
              { value: 'all', label: 'All', activeClass: 'bg-white text-surface-900 shadow-sm' },
              { value: 'expense', label: 'Expense', activeClass: 'bg-white text-expense-600 shadow-sm' },
              { value: 'income', label: 'Income', activeClass: 'bg-white text-income-600 shadow-sm' },
            ].map(tab => (
              <button
                key={tab.value}
                onClick={() => setTransactionFilter(tab.value)}
                className={`py-1.5 rounded-lg font-medium text-xs transition-all ${
                  transactionFilter === tab.value ? tab.activeClass : 'text-surface-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Transactions List */}
          {!transactionsLoading && recentTransactions.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center border border-surface-100">
              <div className="w-14 h-14 bg-surface-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Book className="w-7 h-7 text-surface-400" />
              </div>
              <p className="text-surface-600 font-medium">No transactions yet</p>
              <p className="text-surface-400 text-sm mt-1">Tap + to add your first</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentTransactions
                .filter(t => transactionFilter === 'all' || t.type === transactionFilter)
                .map((transaction) => {
                  const categoryInfo = getCategoryInfo(transaction.category?.name || transaction.category, transaction.type);
                  const CategoryIcon = categoryInfo.icon;
                  // Format date without year
                  const dateStr = formatDate(transaction.date);
                  const shortDate = dateStr.replace(/[-/]\d{4}$/, '').replace(/^\d{4}[-/]/, '');

                  return (
                    <div
                      key={transaction._id}
                      className="bg-white rounded-2xl p-3 flex items-center gap-3 border border-surface-100 active:bg-surface-50 transition-colors cursor-pointer"
                      onClick={() => { if (transactionFilter !== 'all') sessionStorage.setItem('dashboard_tx_filter', transactionFilter); navigate(`/transactions/${transaction._id}`); }}
                    >
                      {/* Category Icon */}
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${categoryInfo.color}15` }}
                      >
                        <CategoryIcon className="w-5 h-5" style={{ color: categoryInfo.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-surface-900 text-sm line-clamp-1">
                          {transaction.description}
                        </p>
                        <p className="text-xs text-surface-400 mt-0.5">
                          {transaction.account?.name || transaction.bankName}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p
                          className="font-bold text-sm"
                          style={{
                            color: transaction.type === 'income'
                              ? config.incomeColor
                              : transaction.type === 'transfer'
                                ? 'var(--accent-bg)'
                                : config.expenseColor
                          }}
                        >
                          {formatAmount(
                            transaction.type === 'income' && isTaxUser
                              ? transaction.amountInINR - (transaction.tdsAmount || 0)
                              : transaction.amountInINR
                          )}
                        </p>
                        <p className="text-[10px] text-surface-400 mt-0.5">
                          {shortDate}
                        </p>
                      </div>
                    </div>
                  );
                })}
              {/* Show message if filter has no results */}
              {recentTransactions.filter(t => transactionFilter === 'all' || t.type === transactionFilter).length === 0 && (
                <div className="text-center py-6">
                  <p className="text-surface-400 text-sm">No {transactionFilter} transactions</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Category Breakdown - Mobile (stacked full width) */}
        <div className={isModernUI ? "mt-4 px-4" : "mt-6 px-4"}>
          <div className="space-y-3">
            <CategoryProgressBars
              data={categoryBreakdown?.expense || []}
              type="expense"
              title="Expense by Category"
              subtitle="this month"
              categories={categories}
              onCategoryClick={(cat) => navigate('/category-transactions', { state: { category: cat, startDate: currentMonthDates.startDate, endDate: currentMonthDates.endDate } })}
            />
            <CategoryProgressBars
              data={categoryBreakdown?.income || []}
              type="income"
              title="Income by Category"
              subtitle="this month"
              categories={categories}
              onCategoryClick={(cat) => navigate('/category-transactions', { state: { category: cat, startDate: currentMonthDates.startDate, endDate: currentMonthDates.endDate } })}
            />
          </div>
        </div>

        {/* Monthly Trend Charts - Mobile */}
        <div className={isModernUI ? "mt-4 px-4 pb-24" : "mt-6 px-4 pb-24"}>
          <div className="flex items-center justify-between mb-2 px-1">
            <h3 className={isModernUI ? "text-sm font-bold text-surface-900" : "text-sm font-semibold text-surface-900"}>Income vs Expense</h3>
            <Link to="/trend" className="text-xs font-medium text-primary-500 hover:text-primary-600">View All</Link>
          </div>
          <div className="bg-white rounded-2xl border border-surface-200 p-3">
            <MonthlyTrendChart data={trendData?.monthlyTrend} combined granularity={trendData?.granularity} compact />
          </div>
        </div>

        </div>
        {/* End White Content Section */}


      {/* Floating Action Button - Add Transaction */}
      <button
        onClick={() => navigate('/transactions/new')}
        className="fab"
        aria-label="Add transaction"
        data-onboarding="add-transaction"
      >
        <Plus className="w-6 h-6" />
      </button>

      </div>

      {/* Desktop Layout - Original */}
      <div className="hidden xl:block space-y-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-surface-500 text-sm font-medium">
              Welcome back
            </p>
            <h1 className="text-3xl font-bold text-surface-900 mt-1">
              {user?.name?.split(' ')[0] || 'User'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Scan & Pay (India / INR only) */}
            {scanPayAvailable && (
            <button
              onClick={() => navigate('/scan-pay')}
              className="h-10 px-4 rounded-xl flex items-center gap-2 text-sm font-semibold transition-colors"
              style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
              title="Pay any UPI QR. No bank linking."
            >
              <ScanLine className="w-4 h-4" />
              Scan & Pay
            </button>
            )}
            {/* Insights Icon */}
            <button
              onClick={() => setIsInsightsOpen(true)}
              className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary-100 text-primary-600 hover:bg-primary-200 transition-colors"
              title="HJ Insights"
            >
              <Sparkles className="w-5 h-5" />
            </button>
{/* FY Badge - DISABLED for simplicity
            <span
              className="px-4 py-2 rounded-xl text-sm font-medium"
              style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
            >
              FY {user?.financialYear || '2025-26'}
            </span>
*/}
          </div>
        </div>

        {/* Summary Card - Compact */}
        <div
          className="rounded-2xl p-5"
          style={{ backgroundColor: 'var(--accent-bg)' }}
        >
          {/* Main Stats Row */}
          <div className="flex items-start justify-between gap-4">
            {/* Total Balance */}
            <div className="flex-shrink-0">
              <span className="text-xs font-medium opacity-70" style={{ color: 'var(--accent-text)' }}>Total Balance</span>
              <p
                className="text-3xl font-bold"
                style={{ color: 'var(--accent-text)' }}
              >
                {formatAmount(totalBalance)}
              </p>
              <p className="text-xs mt-1 opacity-60" style={{ color: 'var(--accent-text)' }}>
                {(summary?.incomeCount || 0) + (summary?.expenseCount || 0)} transactions
              </p>
            </div>

            {/* Income & Expense Cards */}
            <div className="flex gap-3">
              {/* Income Card */}
              <div
                className="rounded-xl px-4 py-3 min-w-[140px]"
                style={{ backgroundColor: 'var(--accent-light)' }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <TrendingUp className="w-4 h-4 text-income-500" />
                  <span className="text-xs font-medium" style={{ color: 'var(--accent-light-text)' }}>Income</span>
                </div>
                <p className="text-lg font-bold" style={{ color: config.incomeColor }}>
                  {formatAmount(totalIncome)}
                </p>
                {isTaxUser && summary?.totalTDS > 0 && (
                  <p className="text-[10px] opacity-70 mt-0.5" style={{ color: 'var(--accent-light-text)' }}>
                    Gross: {formatAmount(summary?.totalIncome || 0)}
                  </p>
                )}
              </div>

              {/* Expense Card */}
              <div
                className="rounded-xl px-4 py-3 min-w-[140px]"
                style={{ backgroundColor: 'var(--accent-light)' }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <TrendingDown className="w-4 h-4 text-expense-500" />
                  <span className="text-xs font-medium" style={{ color: 'var(--accent-light-text)' }}>Expense</span>
                </div>
                <p className="text-lg font-bold" style={{ color: config.expenseColor }}>
                  {formatAmount(totalExpense)}
                </p>
              </div>

              {/* TDS Card - Only for tax user */}
              {isTaxUser && summary?.totalTDS > 0 && (
                <div
                  className="rounded-xl px-4 py-3 min-w-[120px]"
                  style={{ backgroundColor: 'var(--accent-light)' }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Percent className="w-4 h-4 text-amber-500" />
                    <span className="text-xs font-medium" style={{ color: 'var(--accent-light-text)' }}>TDS</span>
                  </div>
                  <p className="text-lg font-bold text-amber-500">
                    {formatAmount(summary?.totalTDS || 0)}
                  </p>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Account Breakdown - Desktop (Matching mobile style) */}
        {visibleAccounts.length >= 1 && (
          <div
            className="rounded-2xl p-5"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium opacity-80" style={{ color: 'var(--accent-text)' }}>
                Account Balances
              </span>
              <Link
                to="/accounts"
                className="text-xs font-medium opacity-80 hover:opacity-100 transition-opacity"
                style={{ color: 'var(--accent-text)' }}
              >
                View All
              </Link>
            </div>
            {/* Account cards - adaptive layout based on count */}
            {visibleAccounts.length === 1 ? (
              /* Single account - full width horizontal card */
              <div
                className="w-full rounded-xl relative overflow-hidden cursor-pointer hover:scale-[1.01] transition-all duration-200 bg-white/15 backdrop-blur-sm border border-white/20"
                onClick={() => navigate('/accounts')}
              >
                {(() => {
                  const account = visibleAccounts[0];
                  const accountColor = account.color || '#3b82f6';
                  const AccountIcon = getIconComponent(account.icon) || (
                    account.type === 'bank' ? Building2
                    : account.type === 'wallet' ? Wallet
                    : account.type === 'cash' ? Banknote
                    : account.type === 'credit_card' ? CreditCard
                    : MoreHorizontal
                  );
                  return (
                    <div className="flex items-center gap-5 p-5">
                      {/* Decorative gradient blobs */}
                      <div
                        className="absolute -right-16 -top-16 w-48 h-48 rounded-full blur-3xl opacity-20"
                        style={{ backgroundColor: accountColor }}
                      />
                      <div
                        className="absolute -left-8 -bottom-8 w-32 h-32 rounded-full blur-2xl opacity-15"
                        style={{ backgroundColor: accountColor }}
                      />
                      {/* Icon container */}
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 relative"
                        style={{ backgroundColor: `${accountColor}25` }}
                      >
                        <AccountIcon className="w-7 h-7" style={{ color: accountColor }} />
                      </div>
                      {/* Account info */}
                      <div className="flex-1 min-w-0 relative">
                        <p className="text-white/60 text-xs font-medium uppercase tracking-wider">
                          {account.type === 'bank' ? 'Bank Account' : account.type === 'wallet' ? 'Wallet' : account.type === 'cash' ? 'Cash' : account.type === 'credit_card' ? 'Credit Card' : 'Account'}
                        </p>
                        <p className="text-white text-lg font-semibold truncate mt-1">
                          {account.name}
                        </p>
                      </div>
                      {/* Balance */}
                      <div className="text-right relative">
                        <p className="text-white/60 text-xs font-medium">
                          Current Balance
                        </p>
                        <p className="text-white text-2xl font-bold mt-1">
                          {formatAmount(account.currentBalance ?? 0)}
                        </p>
                      </div>
                      {/* Arrow indicator */}
                      <ChevronRight className="w-6 h-6 text-white/40" />
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className={
                visibleAccounts.length === 2
                  ? "grid grid-cols-2 gap-4"
                  : visibleAccounts.length <= 4
                    ? "grid grid-cols-4 gap-3"
                    : "flex gap-3 overflow-x-auto pb-1 scrollbar-hide"
              }>
                {[...visibleAccounts].sort((a, b) => (a.currentBalance ?? 0) - (b.currentBalance ?? 0)).map((account) => {
                  const isCard = account.type === 'credit_card';
                  const accountColor = account.color || '#3b82f6';
                  const AccountIcon = getIconComponent(account.icon) || (
                    account.type === 'bank' ? Building2
                    : account.type === 'wallet' ? Wallet
                    : account.type === 'cash' ? Banknote
                    : account.type === 'credit_card' ? CreditCard
                    : MoreHorizontal
                  );

                  // Determine width class based on account count
                  const getWidthClass = (baseWidth) => {
                    if (visibleAccounts.length <= 4) return "w-full";
                    return baseWidth;
                  };

                  // Credit/Debit Card Style - Modern glass card look (matching mobile)
                  if (isCard) {
                    return (
                      <div
                        key={account._id}
                        className={`${visibleAccounts.length > 4 ? 'flex-shrink-0' : ''} ${getWidthClass('w-[160px]')} h-[95px] rounded-xl relative overflow-hidden cursor-pointer hover:scale-[1.02] transition-all duration-200`}
                        style={{
                          background: `linear-gradient(135deg, ${accountColor}ee 0%, ${accountColor}bb 100%)`,
                          boxShadow: `0 4px 12px ${accountColor}40`,
                        }}
                        onClick={() => navigate('/accounts')}
                      >
                        {/* Glossy overlay */}
                        <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent h-1/2" />
                        {/* Chip */}
                        <div className="absolute top-3 left-3 w-6 h-4 rounded-[3px] bg-gradient-to-br from-amber-300 to-amber-500 shadow-sm">
                          <div className="absolute inset-0.5 border-t border-l border-amber-200/50 rounded-[2px]" />
                        </div>
                        {/* NFC */}
                        <div className="absolute top-3 right-3">
                          <Wifi className="w-4 h-4 text-white/50 rotate-90" />
                        </div>
                        {/* Card details */}
                        <div className="absolute bottom-3 left-3 right-3">
                          <p className="text-white/80 text-[10px] font-semibold uppercase tracking-wider truncate mb-0.5">
                            {account.name}
                          </p>
                          <p className="text-white text-lg font-bold">
                            {formatAmount(account.currentBalance ?? 0)}
                          </p>
                        </div>
                      </div>
                    );
                  }

                  // Other account types - Clean minimal card (matching mobile)
                  return (
                    <div
                      key={account._id}
                      className={`${visibleAccounts.length > 4 ? 'flex-shrink-0' : ''} ${getWidthClass('w-[140px]')} h-[95px] rounded-xl p-3 relative overflow-hidden cursor-pointer hover:scale-[1.02] transition-all duration-200 bg-white/15 backdrop-blur-sm border border-white/20`}
                      onClick={() => navigate('/accounts')}
                    >
                      {/* Subtle glow */}
                      <div
                        className="absolute -top-4 -right-4 w-16 h-16 rounded-full blur-xl opacity-30"
                        style={{ backgroundColor: accountColor }}
                      />
                      {/* Icon */}
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center mb-2"
                        style={{ backgroundColor: `${accountColor}30` }}
                      >
                        <AccountIcon className="w-4 h-4" style={{ color: accountColor }} />
                      </div>
                      {/* Details */}
                      <p className="text-white/70 text-[10px] font-medium truncate">
                        {account.name}
                      </p>
                      <p className="text-white text-lg font-bold mt-0.5">
                        {formatAmount(account.currentBalance ?? 0)}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Budget Alerts - Desktop */}
        {budgetAlerts.length > 0 && (
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-surface-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-surface-900">Budget Alerts</h2>
                  <p className="text-sm text-surface-500">Categories near or over budget this month</p>
                </div>
              </div>
              <Link
                to="/stats?type=expense"
                className="text-sm font-medium flex items-center gap-1 transition-colors"
                style={{ color: 'var(--accent-bg)' }}
              >
                View All Stats
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Over Budget Section */}
            {budgetAlerts.filter(a => a.isOverBudget).length > 0 && (
              <div>
                <div className="px-4 py-2 bg-red-50 border-b border-red-100">
                  <p className="text-sm font-medium text-red-600 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Over Budget ({budgetAlerts.filter(a => a.isOverBudget).length})
                  </p>
                </div>
                <div className="divide-y divide-surface-100">
                  {budgetAlerts.filter(a => a.isOverBudget).map((alert) => {
                    const periodLabel = {
                      monthly: '/mo',
                      quarterly: '/qtr',
                      yearly: '/yr'
                    }[alert.budgetPeriod] || '/mo';

                    return (
                      <button
                        key={alert.name}
                        onClick={() => navigate('/budget-category', { state: { category: alert } })}
                        className="w-full flex items-center gap-4 p-4 hover:bg-red-50/50 transition-colors text-left"
                      >
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: alert.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-surface-900">{alert.name}</span>
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                            </div>
                            <span className="font-semibold text-red-500">
                              {formatAmount(alert.spent)} / {formatAmount(alert.budget)}{periodLabel}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm text-surface-500 mb-2">
                            <span>{alert.count} transactions</span>
                            <span className="text-red-500">{alert.percentage}% of budget</span>
                          </div>
                          <div className="h-2 bg-surface-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-red-500" style={{ width: '100%' }} />
                          </div>
                          <p className="text-sm mt-2 text-red-500">
                            ⚠️ Over budget by {formatAmount(alert.spent - alert.budget)}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Near Budget Section */}
            {budgetAlerts.filter(a => !a.isOverBudget).length > 0 && (
              <div>
                <div className="px-4 py-2 bg-amber-50 border-b border-amber-100">
                  <p className="text-sm font-medium text-amber-600 flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full bg-amber-500" />
                    Near Budget ({budgetAlerts.filter(a => !a.isOverBudget).length})
                  </p>
                </div>
                <div className="divide-y divide-surface-100">
                  {budgetAlerts.filter(a => !a.isOverBudget).map((alert) => {
                    const periodLabel = {
                      monthly: '/mo',
                      quarterly: '/qtr',
                      yearly: '/yr'
                    }[alert.budgetPeriod] || '/mo';

                    return (
                      <button
                        key={alert.name}
                        onClick={() => navigate('/budget-category', { state: { category: alert } })}
                        className="w-full flex items-center gap-4 p-4 hover:bg-amber-50/50 transition-colors text-left"
                      >
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: alert.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-surface-900">{alert.name}</span>
                            </div>
                            <span className="font-semibold text-amber-500">
                              {formatAmount(alert.spent)} / {formatAmount(alert.budget)}{periodLabel}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm text-surface-500 mb-2">
                            <span>{alert.count} transactions</span>
                            <span className="text-amber-500">{alert.percentage}% of budget</span>
                          </div>
                          <div className="h-2 bg-surface-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full bg-amber-500"
                              style={{ width: `${alert.percentage}%` }}
                            />
                          </div>
                          <p className="text-sm mt-2 text-amber-500">
                            ⏳ {formatAmount(alert.budget - alert.spent)} remaining - try to reduce spending
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actionable Insights - Desktop */}
        <ActionableInsights
          statsData={statsData}
          summary={summary}
          recurringTransactions={recurringTransactionsForInsights}
          formatAmount={formatAmount}
        />

        {/* Category Breakdown - Stacked */}
        <div className="space-y-6">
          <CategoryProgressBars
            data={categoryBreakdown?.expense || []}
            type="expense"
            title="Expense by Category"
            subtitle="this month"
            categories={categories}
            onCategoryClick={(cat) => navigate('/category-transactions', { state: { category: cat, startDate: currentMonthDates.startDate, endDate: currentMonthDates.endDate } })}
          />
          <CategoryProgressBars
            data={categoryBreakdown?.income || []}
            type="income"
            title="Income by Category"
            subtitle="this month"
            categories={categories}
            onCategoryClick={(cat) => navigate('/category-transactions', { state: { category: cat, startDate: currentMonthDates.startDate, endDate: currentMonthDates.endDate } })}
          />
        </div>

        {/* Monthly Trend Charts - Desktop */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-surface-900">Income vs Expense Trend</h2>
            <Link to="/trend" className="text-sm font-medium text-primary-500 hover:text-primary-600">View All</Link>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-surface-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold" style={{ color: config.incomeColor || '#10b981' }}>Income</h3>
              </div>
              <MonthlyTrendChart data={trendData?.monthlyTrend} type="income" granularity={trendData?.granularity} />
            </div>
            <div className="bg-white rounded-2xl border border-surface-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold" style={{ color: config.expenseColor || '#ef4444' }}>Expense</h3>
              </div>
              <MonthlyTrendChart data={trendData?.monthlyTrend} type="expense" granularity={trendData?.granularity} />
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Transactions */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-surface-200 overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-surface-100">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-surface-900">Recent Transactions</h2>
                <div className="relative flex items-center">
                  <button
                    onClick={() => toggleTooltip('desktop-tx')}
                    className="flex items-center text-surface-300 hover:text-surface-500 transition-colors"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                  {activeTooltip === 'desktop-tx' && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2.5 py-1.5 bg-surface-800 text-white text-[10px] rounded-lg whitespace-nowrap z-50 shadow-lg">
                      Showing this month only
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-surface-800 rotate-45" />
                    </div>
                  )}
                </div>
              </div>
              <Link
                to="/transactions"
                className="text-sm text-surface-500 hover:text-surface-900 font-medium flex items-center gap-1 transition-colors"
              >
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {!transactionsLoading && recentTransactions.length === 0 ? (
              <div className="text-center py-12 px-6">
                <div className="w-16 h-16 bg-theme-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Book className="w-8 h-8 text-theme-400" />
                </div>
                <p className="text-surface-900 font-medium">No transactions yet</p>
                <p className="text-surface-500 text-sm mt-1">Start by adding your first transaction</p>
                <Link
                  to="/transactions"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-theme-600 text-white rounded-xl text-sm font-medium hover:bg-theme-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Transaction
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-surface-100">
                {recentTransactions.map((transaction) => (
                  <div
                    key={transaction._id}
                    className="flex items-center justify-between p-4 hover:bg-surface-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          transaction.type === 'income'
                            ? 'bg-income-100'
                            : 'bg-expense-100'
                        }`}
                      >
                        {transaction.type === 'income' ? (
                          <ArrowUpRight className="w-5 h-5 text-income-600" />
                        ) : (
                          <ArrowDownRight className="w-5 h-5 text-expense-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-surface-900">
                          {transaction.description}
                        </p>
                        <p className="text-sm text-surface-500">
                          {formatDate(transaction.date)}{transaction.time && `, ${formatTime(transaction.time)}`} • {transaction.account?.name || transaction.bankName}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className="font-semibold text-lg"
                        style={{
                          color: transaction.type === 'income' ? config.incomeColor : config.expenseColor
                        }}
                      >
                        {formatAmount(
                          transaction.type === 'income' && isTaxUser
                            ? transaction.amountInINR - (transaction.tdsAmount || 0)
                            : transaction.amountInINR
                        )}
                      </p>
                      {isTaxUser && transaction.type === 'income' && transaction.tdsAmount > 0 && (
                        <>
                          <p className="text-xs text-surface-400">
                            TDS: {formatAmount(transaction.tdsAmount)}
                          </p>
                          <p className="text-xs text-surface-500">
                            Total: {formatAmount(transaction.amountInINR)}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions - Compact Cards */}
          <div className="bg-white rounded-2xl border border-surface-200 p-5">
            <h2 className="text-lg font-semibold text-surface-900 mb-4">
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/stats"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-light-text)' }}
                >
                  <BarChart3 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Stats</h3>
                  <p className="text-xs text-surface-500 truncate">Spending breakdown</p>
                </div>
              </Link>

              <Link
                to="/transactions"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 bg-income-100 rounded-lg flex items-center justify-center group-hover:bg-income-200 transition-colors">
                  <TrendingUp className="w-5 h-5 text-income-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Transactions</h3>
                  <p className="text-xs text-surface-500 truncate">Income & expenses</p>
                </div>
              </Link>

              <Link
                to="/recurring"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-light-text)' }}
                >
                  <Repeat className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Recurring</h3>
                  <p className="text-xs text-surface-500 truncate">Scheduled payments</p>
                </div>
              </Link>

              <Link
                to="/ledger"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-light-text)' }}
                >
                  <BookOpen className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Ledger</h3>
                  <p className="text-xs text-surface-500 truncate">Account statements</p>
                </div>
              </Link>

              <Link
                to="/receivables"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 bg-income-100 rounded-lg flex items-center justify-center group-hover:bg-income-200 transition-colors">
                  <ArrowDownLeft className="w-5 h-5 text-income-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Receivables</h3>
                  <p className="text-xs text-surface-500 truncate">Money to receive</p>
                </div>
              </Link>

              <Link
                to="/debts"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 bg-expense-100 rounded-lg flex items-center justify-center group-hover:bg-expense-200 transition-colors">
                  <ArrowUpRight className="w-5 h-5 text-expense-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Debts</h3>
                  <p className="text-xs text-surface-500 truncate">Money to pay</p>
                </div>
              </Link>

              <Link
                to="/passcode"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 bg-surface-100 rounded-lg flex items-center justify-center group-hover:bg-surface-200 transition-colors">
                  <Lock className="w-5 h-5 text-surface-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">App Lock</h3>
                  <p className="text-xs text-surface-500 truncate">Passcode settings</p>
                </div>
              </Link>

              <Link
                to="/settings/theme"
                className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-light-text)' }}
                >
                  <Palette className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-surface-900 text-sm">Theme</h3>
                  <p className="text-xs text-surface-500 truncate">Theme settings</p>
                </div>
              </Link>

              {isTaxUser && (
                <Link
                  to="/tax"
                  className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 hover:border-surface-200 hover:shadow-md transition-all group"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                    style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-light-text)' }}
                  >
                    <Calculator className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-surface-900 text-sm">Tax Report</h3>
                    <p className="text-xs text-surface-500 truncate">View calculations</p>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Insights Modal */}
      <InsightsModal
        isOpen={isInsightsOpen}
        onClose={() => setIsInsightsOpen(false)}
      />
    </>
  );
}
